import { Lci3Report } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { UseCalculatedFieldsReturn } from '@features/campaigns';
import { z } from 'zod';

export type MinimalReport = {
  visitMetrics: Pick<
    Lci3Report['visitMetrics'],
    'impressions' | 'uniqueUsers' | 'incrementalVisits' | 'projectedVisits'
  >;
  salesMetrics: Pick<
    NonNullable<Lci3Report['salesMetrics']>,
    'totalProjectedSales' | 'incrementalSales'
  > | null;
  subdimensions: {
    visitMetrics: Pick<
      Lci3Report['subdimensions'][number]['visitMetrics'],
      'impressions' | 'uniqueUsers' | 'incrementalVisits' | 'projectedVisits'
    >;
    salesMetrics: Pick<
      NonNullable<Lci3Report['subdimensions'][number]['salesMetrics']>,
      'totalProjectedSales' | 'incrementalSales'
    > | null;
  }[];
};

export const getYoutubeProjectionFactor = (
  youtubeImpressions: number | null,
  totalImpressions: number
): number | null => {
  if (!youtubeImpressions) {
    return null;
  }
  if (youtubeImpressions < totalImpressions) {
    throw new Error(
      'YouTube impressions cannot be less than total impressions'
    );
  }

  return youtubeImpressions / totalImpressions;
};

export const applyYoutubeProjectionFactor = <T extends MinimalReport>(
  youtubeImpressions: number | null,
  report: T
): T => {
  const youtubeProjectionFactor = getYoutubeProjectionFactor(
    youtubeImpressions,
    report.visitMetrics.impressions
  );

  if (!youtubeProjectionFactor) {
    return report;
  }

  return {
    ...report,
    visitMetrics: transformVisitMetrics(
      report.visitMetrics,
      youtubeProjectionFactor
    ),
    salesMetrics: transformSalesMetrics(
      report.salesMetrics,
      youtubeProjectionFactor
    ),
    subdimensions: transformSubdimensions(
      report.subdimensions,
      youtubeProjectionFactor
    ),
  };
};

export const transformSubdimensions = <
  T extends MinimalReport['subdimensions'][number],
>(
  subdimensions: T[],
  youtubeProjectionFactor: number
): T[] => {
  return subdimensions.map(subdimension => ({
    ...subdimension,
    visitMetrics: transformVisitMetrics(
      subdimension.visitMetrics,
      youtubeProjectionFactor
    ),
    salesMetrics: transformSalesMetrics(
      subdimension.salesMetrics,
      youtubeProjectionFactor
    ),
  }));
};

export const transformVisitMetrics = <T extends MinimalReport['visitMetrics']>(
  visitMetrics: T,
  youtubeProjectionFactor: number
): T => ({
  ...visitMetrics,
  impressions: transformValue(
    visitMetrics.impressions,
    youtubeProjectionFactor
  ),
  uniqueUsers: transformValue(
    visitMetrics.uniqueUsers,
    youtubeProjectionFactor
  ),
  incrementalVisits: transformValue(
    visitMetrics.incrementalVisits,
    youtubeProjectionFactor
  ),
  projectedVisits: transformValue(
    visitMetrics.projectedVisits,
    youtubeProjectionFactor
  ),
});

export const transformSalesMetrics = <T extends MinimalReport['salesMetrics']>(
  salesMetrics: T,
  youtubeProjectionFactor: number
): T => {
  if (!salesMetrics) {
    return salesMetrics;
  }

  return {
    ...salesMetrics,
    totalProjectedSales: {
      ...salesMetrics.totalProjectedSales,
      total: transformValue(
        salesMetrics.totalProjectedSales.total,
        youtubeProjectionFactor
      ),
      online: transformValue(
        salesMetrics.totalProjectedSales.online,
        youtubeProjectionFactor
      ),
      inStore: transformValue(
        salesMetrics.totalProjectedSales.inStore,
        youtubeProjectionFactor
      ),
    },
    incrementalSales: {
      ...salesMetrics.incrementalSales,
      total: transformValue(
        salesMetrics.incrementalSales.total,
        youtubeProjectionFactor
      ),
      online: transformValue(
        salesMetrics.incrementalSales.online,
        youtubeProjectionFactor
      ),
      inStore: transformValue(
        salesMetrics.incrementalSales.inStore,
        youtubeProjectionFactor
      ),
    },
  };
};

type CalculatedFields = Omit<UseCalculatedFieldsReturn['data'], 'adSpend'>;

export const transformCalculatedFields = <T extends CalculatedFields>(
  calculatedFields: T,
  youtubeProjectionFactor: number
): T => ({
  ...calculatedFields,
  costPerVisit: transformValueInverted(
    calculatedFields.costPerVisit,
    youtubeProjectionFactor
  ),
  costPerIncrementalVisit: transformValueInverted(
    calculatedFields.costPerIncrementalVisit,
    youtubeProjectionFactor
  ),
  returnOnAdSpend: transformValue(
    calculatedFields.returnOnAdSpend,
    youtubeProjectionFactor
  ),
  incrementalReturnOnAdSpend: transformValue(
    calculatedFields.incrementalReturnOnAdSpend,
    youtubeProjectionFactor
  ),
});

export const transformValue = (
  value: number | null,
  multiplier: number
): number | null => (value ? value * multiplier : value);

const transformValueInverted = (
  value: number | null,
  multiplier: number
): number | null => (value ? value / multiplier : value);

export const youtubeImpressionsSchema = (totalImpressions: number) =>
  z
    .number()
    .min(
      totalImpressions,
      'YouTube impressions must be greater than or equal to total impressions'
    )
    .nullable();
